import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  REVIEW_TYPE_OF_PROVIDER,
  REVIEW_TYPE_OF_CUSTOMER,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
  propTypes,
} from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import {
  getDetailCustomFieldValue,
  getFieldValue,
  pickCustomFieldProps,
} from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import { richText } from '../../util/richText';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  LayoutSingleColumn,
  LayoutSideNavigation,
  NamedRedirect,
  PortfolioGallery,
  CustomExtendedDataSection,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import { normalizeCslbLicense } from '../../util/cslb';

import css from './ProfilePage.module.css';
import VendorProfileSection from './VendorProfileSection';
import { buildVendorMetaDescription } from './vendorMetaDescription';
import {
  getServiceLabel,
  getBannerGradient,
} from '../../config/vendorProfileOptions';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

const VendorHeroHeader = ({ user, displayName, bio, publicData, showEditLink, requestId, vendorId }) => {
  const [sendState, setSendState] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const businessName = publicData?.businessName || displayName;

  // locationDisplay stores "City, State" saved to publicData on profile save
  const locationDisplay = publicData?.locationDisplay;
  const servicesOffered = publicData?.services_offered_vendor || publicData?.servicesOffered || [];
  // Vendor-entered, so it may carry a classification prefix (e.g. "C-27 Lic#1039612").
  // Null when unparseable — better no link than one the homeowner can't verify.
  const contractorLicense = normalizeCslbLicense(
    publicData?.cslbLicenseNumber ||
      publicData?.contractor_license_number ||
      publicData?.contractorLicenseNumber
  );

  // Customizable banner: uploaded image wins, else a theme gradient (with default).
  const bannerImageUrl = publicData?.bannerImage?.url;
  const bannerPos = publicData?.bannerPosition;
  const heroBgStyle = bannerImageUrl
    ? {
        backgroundImage: `url(${bannerImageUrl})`,
        backgroundPosition: bannerPos ? `${bannerPos.x}% ${bannerPos.y}%` : 'center',
      }
    : { backgroundImage: getBannerGradient(publicData?.bannerStyle) };

  const locationString = locationDisplay || '';

  // Trust row: Google's rating, the vendor's own operating history, CSLB record — never CAFD claims
  const googleRating = publicData?.googleRating;
  const googleReviewCount = publicData?.googleReviewCount;
  const licensedSince = publicData?.licensedSince;
  const operatingSince = publicData?.operatingSince; // vendor's own claim, takes precedence
  const tenure = operatingSince
    ? `In business since ${operatingSince}`
    : licensedSince
      ? `Licensed since ${licensedSince}`
      : null;

  const handleSendRequest = async () => {
    if (sendState === 'sending' || sendState === 'sent') return;
    setSendState('sending');
    try {
      const { sendRequestToVendor } = await import('../../util/api');
      await sendRequestToVendor({ requestId, vendorId });
      setSendState('sent');
    } catch (err) {
      console.error('Failed to send request to vendor:', err);
      setSendState('error');
      setTimeout(() => setSendState('idle'), 3000);
    }
  };

  return (
    <div className={css.vendorHero} style={heroBgStyle}>
      <div className={css.vendorHeroContent}>
        <div className={css.vendorHeroTop}>
          {user?.profileImage?.attributes?.variants?.default?.url ? (
            <div
              className={css.vendorHeroLogo}
              /* logos with white/light elements need a dark tile (publicData.logoBackground) */
              style={publicData?.logoBackground ? { backgroundColor: publicData.logoBackground } : undefined}
            >
              <img
                src={user.profileImage.attributes.variants.default.url}
                alt={businessName}
              />
            </div>
          ) : (
            <AvatarLarge className={css.vendorHeroAvatar} user={user} disableProfileLink />
          )}
          <div className={css.vendorHeroInfo}>
            <h1 className={css.vendorHeroName}>{businessName}</h1>
            {locationString && (
              <p className={css.vendorHeroLocation}>{locationString}</p>
            )}
            {(googleRating || contractorLicense || tenure) && (
              <div className={css.vendorTrustRow}>
                {googleRating && (
                  <span className={css.trustItem}>
                    <span className={css.trustStar}>★</span>
                    {` ${googleRating}`}
                    {googleReviewCount ? ` (${googleReviewCount} Google reviews)` : ' on Google'}
                  </span>
                )}
                {tenure && <span className={css.trustTenure}>{tenure}</span>}
                {contractorLicense && (
                  <a
                    className={css.trustLink}
                    href="https://www.cslb.ca.gov/onlineservices/checklicenseII/checklicense.aspx"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    CSLB #{contractorLicense} — verify ↗
                  </a>
                )}
              </div>
            )}
            {servicesOffered.length > 0 && (
              <div className={css.vendorHeroChips}>
                {servicesOffered.map(s => (
                  <span key={s} className={css.vendorHeroChip}>{getServiceLabel(s)}</span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={css.vendorHeroActions}>
          {requestId ? (
            <button
              onClick={handleSendRequest}
              className={classNames(css.vendorHeroCta, css.sendRequestCta, {
                [css.sendRequestSending]: sendState === 'sending',
                [css.sendRequestSent]: sendState === 'sent',
                [css.sendRequestError]: sendState === 'error',
              })}
              disabled={sendState === 'sending' || sendState === 'sent'}
            >
              {sendState === 'idle' && 'Send My Request'}
              {sendState === 'sending' && 'Sending...'}
              {sendState === 'sent' && 'Request Sent \u2713'}
              {sendState === 'error' && 'Failed \u2013 Try Again'}
            </button>
          ) : (
            <NamedLink name="RequestIntakeBasePage" className={css.vendorHeroCta}>
              Start a Request
            </NamedLink>
          )}
          {showEditLink && (
            <NamedLink name="VendorProfilePage" className={css.vendorHeroEdit}>
              Edit Profile
            </NamedLink>
          )}
        </div>
      </div>
    </div>
  );
};

export const AsideContent = props => {
  const { user, displayName, showLinkToProfileSettingsPage } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {showLinkToProfileSettingsPage ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles } = props;
  const { provider: isProviderUserType } = userTypeRoles || {};
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);

  // Only show reviews for provider/vendor profiles
  if (!isProviderUserType || reviewsOfProvider.length === 0) {
    return null;
  }

  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles } = props;
  const { provider: isProviderUserType } = userTypeRoles || {};

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);

  // Only show reviews for provider/vendor profiles
  if (!isProviderUserType || reviewsOfProvider.length === 0) {
    return null;
  }

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </Heading>

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
        <Reviews reviews={reviewsOfProvider} />
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig, intl } = props;

  // Check if this is a provider/vendor user type - render enhanced vendor profile
  const userType = publicData?.userType;
  const isProvider = userType === 'provider' || userType === 'vendor';

  if (isProvider) {
    return <VendorProfileSection {...props} />;
  }

  // Default rendering for non-vendor users
  const shouldPickUserField = fieldConfig =>
    ['public', 'metadata'].includes(fieldConfig?.scope) &&
    fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      userFieldConfig,
      'userType',
      shouldPickUserField
    ) || [];

  const pickUserFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, userTypeConfig = {}, showConfig = {} } = config;
    const { limitToUserTypeIds, userTypeIds } = userTypeConfig;
    const userType = publicData.userType;
    const isTargetUserType = !limitToUserTypeIds || userTypeIds.includes(userType);

    const { label, displayInProfile } = showConfig;
    const publicDataValue = getFieldValue(publicData, key);
    const metadataValue = getFieldValue(metadata, key);
    const value = publicDataValue !== null ? publicDataValue : metadataValue;

    if (displayInProfile && isTargetUserType && value !== null) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ProfilePage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };
  const sectionDetailsProps = {
    ...props,
    fieldConfigs: userFieldConfig,
    heading: 'ProfilePage.detailsTitle',
    rootClassName: css.userFieldSection,
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="profilePage"
      pickExtendedDataFields={pickUserFields}
      rootClassName={css.userFieldSection}
    />
  );
};

const VendorReviewSummary = ({ reviews }) => {
  const providerReviews = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  if (providerReviews.length === 0) return null;

  const totalRating = providerReviews.reduce((sum, r) => sum + (r.attributes.rating || 0), 0);
  const avgRating = (totalRating / providerReviews.length).toFixed(1);
  const fullStars = Math.floor(avgRating);
  const hasHalf = avgRating - fullStars >= 0.5;

  const stars = [];
  for (let i = 0; i < 5; i++) {
    if (i < fullStars) stars.push('\u2605');
    else if (i === fullStars && hasHalf) stars.push('\u2605');
    else stars.push('\u2606');
  }

  return (
    <div className={css.reviewSummary}>
      <div className={css.reviewSummaryLeft}>
        <span className={css.reviewSummaryRating}>{avgRating}</span>
        <div className={css.reviewSummaryStars}>
          <span className={css.reviewStars}>{stars.join('')}</span>
          <span className={css.reviewCount}>{providerReviews.length} review{providerReviews.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
      <div className={css.reviewSummaryRight}>
        <span className={css.responseTimeBadge}>
          <span className={css.responseTimeDot} />
          Usually responds within 24h
        </span>
      </div>
    </div>
  );
};

export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    userTypeRoles,
  } = props;

  const hasListings = listings.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  const isVendorLayout = userTypeRoles?.provider;

  return (
    <div>
      {!isVendorLayout && (
        <H2 as="h1" className={css.desktopHeading}>
          <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
        </H2>
      )}
      {hasBio && !isVendorLayout ? <p className={css.bio}>{bioWithLinks}</p> : null}

      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
        />
      ) : null}

      {isVendorLayout && <VendorReviewSummary reviews={reviews} />}

      {isVendorLayout && <PortfolioGallery images={publicData?.portfolioImages} />}

      {hasListings && !isVendorLayout ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
          </H4>
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard listing={l} showAuthorInfo={false} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hideReviews ? null : isMobileLayout ? (
        <MobileReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
        />
      ) : (
        <DesktopReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
          intl={intl}
        />
      )}
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const urlParams = searchParams ? new URLSearchParams(searchParams) : null;
  const isStorefront = urlParams?.get('mode') === 'storefront';
  const requestId = urlParams?.get('requestId') || null;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  const isVendorProfile = userTypeRoles?.provider;

  if (isVendorProfile) {
    const vendorMetaDescription = buildVendorMetaDescription(
      profileUser,
      intl,
      config.marketplaceName
    );
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        title={schemaTitle}
        description={vendorMetaDescription}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'ProfilePage',
          mainEntity: {
            '@type': 'Person',
            name: profileUser?.attributes?.profile?.displayName,
          },
          name: schemaTitle,
        }}
      >
        <LayoutSingleColumn
          topbar={<TopbarContainer />}
          footer={<FooterContainer />}
        >
          <VendorHeroHeader
            user={profileUser}
            displayName={displayName}
            bio={bio}
            publicData={publicData}
            showEditLink={mounted && isCurrentUser}
            requestId={requestId}
            vendorId={pathParams.id}
          />
          <div className={css.vendorMainContent}>
            {bio && (
              <div className={css.aboutSection}>
                <h2 className={css.aboutTitle}>About</h2>
                <p className={css.aboutText}>
                  {richText(bio, {
                    linkify: true,
                    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                    longWordClass: css.longWord,
                  })}
                </p>
              </div>
            )}
            <MainContent
              bio={null}
              displayName={displayName}
              userShowError={userShowError}
              publicData={publicData}
              metadata={metadata}
              userFieldConfig={userFields}
              hideReviews={hasNoViewingRightsOnPrivateMarketplace}
              intl={intl}
              userTypeRoles={userTypeRoles}
              {...rest}
            />
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={<TopbarContainer />}
        sideNav={
          <AsideContent
            user={profileUser}
            showLinkToProfileSettingsPage={mounted && isCurrentUser}
            displayName={displayName}
          />
        }
        footer={<FooterContainer />}
      >
        <MainContent
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          hideReviews={hasNoViewingRightsOnPrivateMarketplace}
          intl={intl}
          userTypeRoles={userTypeRoles}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
